import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreHoriz as MoreHorizIcon,
  ChatBubble as ChatBubbleIcon,
} from "@mui/icons-material";
import { LANG } from "./constants";

const ConversationItem = ({
  conv,
  isActive,
  onSelect,
  onDelete,
  onRename,
  lang,
}) => {
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState(null);

  return (
    <Box
      onClick={() => onSelect(conv)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1.5,
        py: 0.875,
        mx: 1,
        mb: 0.25,
        borderRadius: 2,
        cursor: "pointer",
        position: "relative",
        bgcolor: isActive
          ? alpha(theme.palette.primary.main, 0.1)
          : "transparent",
        border: `1px solid ${
          isActive ? alpha(theme.palette.primary.main, 0.2) : "transparent"
        }`,
        minWidth: 0,
        "&:hover": {
          bgcolor: isActive
            ? alpha(theme.palette.primary.main, 0.13)
            : alpha(theme.palette.text.primary, 0.04),
          "& .conv-actions": { opacity: 1 },
        },
        "&::before": isActive
          ? {
              content: '""',
              position: "absolute",
              left: -1,
              top: "18%",
              bottom: "18%",
              width: 3,
              borderRadius: "0 3px 3px 0",
              bgcolor: "primary.main",
              boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.5)}`,
            }
          : {},
        transition: "all 0.12s ease",
      }}
    >
      <ChatBubbleIcon
        sx={{
          fontSize: 12,
          color: isActive ? "primary.main" : "text.disabled",
          flexShrink: 0,
          opacity: isActive ? 1 : 0.5,
        }}
      />

      <Typography
        variant="body2"
        noWrap
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: "0.8125rem",
          fontWeight: isActive ? 600 : 400,
          color: isActive ? "primary.main" : "text.secondary",
          lineHeight: 1.4,
        }}
      >
        {conv.title || "Untitled"}
      </Typography>

      <Box
        className="conv-actions"
        sx={{
          opacity: { xs: 1, sm: 0 },
          transition: "opacity 0.15s",
          flexShrink: 0,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <IconButton
          size="small"
          sx={{
            width: 22,
            height: 22,
            color: "text.disabled",
            "&:hover": { color: "text.secondary", bgcolor: "action.hover" },
          }}
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchor(e.currentTarget);
          }}
        >
          <MoreHorizIcon sx={{ fontSize: 13 }} />
        </IconButton>
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          elevation: 0,
          sx: {
            minWidth: 160,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow:
              theme.palette.mode === "dark"
                ? `0 8px 24px ${alpha(theme.palette.common.black, 0.4)}`
                : `0 8px 24px ${alpha(theme.palette.common.black, 0.1)}`,
            borderRadius: 2,
          },
        }}
      >
        <MenuItem
          onClick={() => {
            onRename(conv);
            setMenuAnchor(null);
          }}
          sx={{ gap: 1.5, fontSize: "0.8125rem", borderRadius: 1, mx: 0.5 }}
        >
          <EditIcon sx={{ fontSize: 14, color: "text.secondary" }} />
          {lang?.renameConversation || LANG.renameConversation}
        </MenuItem>

        <MenuItem
          onClick={() => {
            onDelete(conv);
            setMenuAnchor(null);
          }}
          sx={{
            gap: 1.5,
            fontSize: "0.8125rem",
            color: "error.main",
            borderRadius: 1,
            mx: 0.5,
          }}
        >
          <DeleteIcon sx={{ fontSize: 14 }} />
          {lang?.deleteConversation || LANG.deleteConversation}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ConversationItem;
